import { useDebounce } from '@fox/atoms/hooks/use-debounce';
import { Button } from '@fox/atoms/src/button/index';
import { Chip } from '@fox/atoms/src/chip/index';
import { Divider } from '@fox/atoms/src/divider/index';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { Select } from '@fox/atoms/src/select/index';
import { Skeleton } from '@fox/atoms/src/skeleton/index';
import { useTempDataContext } from '@fox/atoms/src/temp-data-context/index';
import { TextField } from '@fox/atoms/src/text-field/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { ensureArray } from '@fox/atoms/utils/array';
import { filterEmpty } from '@fox/atoms/utils/filter-empty';
import { strIsNullOrEmpty, tryParseJSONObject } from '@fox/atoms/utils/string';
import { stripTypenames } from '@fox/atoms/utils/strip-type-names';
import { IBlockDefinition, IBlockProps } from '@fox/blocks/contracts/block';
import { useI18n } from '@fox/extensions/src/use-i18n';
import { DebitForm } from '@fox/finance/src/components/add-payments/direct-debit';
import { FormPaymentContext } from '@fox/finance/src/components/form-payment-context';
import { IntentAdd } from '@fox/finance/src/components/intent-component/index';
import { DESIGN_MODE_INTENT, FRIENDLY_PAYMENT_METHODS } from '@fox/finance/src/constants';
import { PaymentMethod } from '@fox/finance/src/contracts';
import { FlexColumnSmallSpaced, FlexColumnSpaced, FlexRowSpacedCentered, VerticalInset } from '@fox/layout/src/flex';
import { useQuery } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import {
	ACCOUNT_BLOCK_TYPE,
	CREATE_PEOPLE_BLOCK,
	CREATE_PERSON_BLOCK,
	PAYMENT_BLOCK_ANSWER_TYPE,
	SUBSCRIPTION_ANSWER_TYPE,
	TRANSACTION_BLOCK_TYPE
} from '../../../form-block-types';
import { findRenderDependencies } from '../../../utils';
import { useFormFinancialContext } from '../../form-financial-context/form-financial-context';
import { IField, IQuestionAnswer } from '../../form-root/contracts';
import { FormRenderMode, QuestionStyle, QuestionStyleTypes } from '../../form-runtime-context/contracts';
import { useFormRuntimeContext, useQuestionStyle } from '../../form-runtime-context/form-runtime-context';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';

import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { ensureAnswers } from '../form-block/util/ensure-answers';
import { isAllowZeroPayment } from '../form-block/validators/validate-payment-block';
import { SubscriptionAnswer } from '../subscription-block/contracts';
import {
	OverrideOption,
	PaymentAnswer,
	PaymentAnswerData,
	PaymentBlockMetadataType,
	PaymentBlockRuntimeProps,
	STATEMENT_PROVIDER
} from './contracts';
import { findBlockValue, getPaymentAmounts, PaymentComponents } from './helpers';
import PaymentTotalsSummary from './payment-totals-summary';
import SavedPaymentDetails from './saved-payment-details';

const ZeroPayment: FC = () => {
	return <div>Payment details not required</div>;
};

export const LineItemsIdentifier = 'lineItems';

export const PaymentBlockRuntime = (props: IBlockProps<IField> & PaymentBlockRuntimeProps) => {
	const { submitForm, owner, target, inLocalSurveyMode, formRenderMode, getAnswer, answerQuestion, getAnswers, sections, layout, formRequestId } =
		useFormRuntimeContext();
	const { getProviders, fetchCurrencies, fetchTaxRates, setupIntent, updateIntent, completeDebit, completeCardIntent, validateDiscount } =
		useFormFinancialContext();
	const tempDataContext = useTempDataContext();
	const fieldProps = props.props;
	const dependencies = findRenderDependencies(sections, [
		CREATE_PEOPLE_BLOCK,
		CREATE_PERSON_BLOCK,
		ACCOUNT_BLOCK_TYPE,
		TRANSACTION_BLOCK_TYPE,
		SUBSCRIPTION_ANSWER_TYPE
	]);
	useFormBlockReRenderer([...dependencies, props.props.name], fieldProps);
	const [error, setError] = useState(false);
	const [discountCode, setDiscountCode] = useState('');
	const answers = getAnswers();
	const i18n = useI18n();

	const tempData = tempDataContext?.[fieldProps.name];
	const fieldMeta = tryParseJSONObject<PaymentBlockMetadataType>(fieldProps.metadataJson, {});
	const metaProviders = [...filterEmpty(ensureArray(fieldMeta.provider)), ...(fieldMeta.invoice?.enabled ? [STATEMENT_PROVIDER] : [])];
	const [paymentAttempts, setPaymentAttempts] = useState(0);

	const selectedAnswer: IQuestionAnswer<PaymentAnswerData> | undefined = getAnswer(fieldProps.name);
	const referenceOverrideType = fieldMeta.invoice?.allowReferenceOverride;

	const discounts = selectedAnswer?.data?.discounts ?? [];
	// TODO: Remove once we have "initialAnswers" for form answers. Soon™
	useEffect(() => {
		if (formRenderMode !== FormRenderMode.design && !selectedAnswer && fieldMeta?.invoice?.enabled && metaProviders?.length === 1) {
			answerQuestion({
				data: {
					method: PaymentMethod.Statement
				},
				questionName: fieldProps.name,
				type: PAYMENT_BLOCK_ANSWER_TYPE
			});
		}
	}, [metaProviders]);

	//dont like we have to do all this here
	const formSubmit = async (intentResult?: unknown) => {
		if (submitForm) {
			const answers = getAnswers();
			if (intentResult) {
				const index = answers.findIndex(a => a.questionName === selectedAnswer?.questionName);
				if (index > -1) {
					const parsed = tryParseJSONObject<any>(answers[index].answer?.answerJson, {});
					const updated = { ...(parsed ?? {}), ...(intentResult as any) };
					answers[index]['answer'] = { ...(answers[index]?.answer ?? {}), answerJson: JSON.stringify(updated) };
				}
			}
			if (tempDataContext) {
				tempDataContext['contextSubmission'] = true;
				tempDataContext[fieldProps.name] = { ...parsedAnswer, ...(intentResult as any) };
			}

			const toSubmit = await ensureAnswers(answers, i18n.countryCode);

			return submitForm(toSubmit);
		} else {
			return Promise.resolve(undefined);
		}
	};
	// this should be part of form runtime context
	const readyToQuery = formRenderMode !== FormRenderMode.runtime ? true : !!props.token || !!inLocalSurveyMode;

	const parsedAnswer = tryParseJSONObject<PaymentAnswer>(selectedAnswer?.answer?.answerJson, {});
	const {
		data: providers,
		isFetching: providersLoading,
		isFetched,
		isError: providersError
	} = useQuery({
		queryKey: ['financial_providers', props.token, metaProviders],
		queryFn: async () => {
			try {
				const result = await getProviders({ provider: metaProviders, token: props.token });
				const filtered =
					result?.filter(p => {
						if (p.method === PaymentMethod.CreditCard || p.method === PaymentMethod.OneTimePayment) {
							return PaymentComponents[p.providerExtension]?.[p.method];
						} else {
							return true;
						}
					}) ?? [];
				if (fieldMeta.invoice?.enabled) {
					filtered.push(STATEMENT_PROVIDER);
				}
				return filtered;
			} catch (e) {
				console.error('Error fetching providers: ', e);
				return [];
			}
		},
		staleTime: Infinity,
		enabled: readyToQuery
	});

	const {
		data: currencies,
		isLoading: currenciesLoading,
		isError: currenciesError
	} = useQuery({
		queryKey: ['fetchCurrencies'],
		queryFn: async () => {
			const result = await fetchCurrencies(props.token);
			if (!result?.default) throw Error('Unable to fetch supported currencies');
			return result;
		},
		staleTime: Infinity
	});

	const {
		data: taxRates,
		isLoading: taxRatesLoading,
		isError: taxRatesError
	} = useQuery({
		queryKey: ['fetchTaxRates'],
		queryFn: fetchTaxRates,
		staleTime: Infinity
	});

	const labelStyle = useQuestionStyle(QuestionStyleTypes.label, {}, props.id);
	const labelStyles: Record<string, QuestionStyle> = {
		'&.MuiTypography-root.MuiTypography-body1': {
			...labelStyle
		}
	};

	const availableMethods = metaProviders?.map(p => p?.method) ?? [];
	const matchingMetaProvider =
		availableMethods?.length > 1 ? metaProviders?.find(p => p.method === selectedAnswer?.data?.method) : metaProviders?.[0];
	const selectedProvider = providers?.find(p => p.provider === matchingMetaProvider?.provider && p.method === matchingMetaProvider?.method);

	const subscriptionBlocks = answers.filter(a => a.type === 'Subscription');
	const subscriptionSelections: string[] = [];
	for (const sub of subscriptionBlocks) {
		const subAnswer = tryParseJSONObject<SubscriptionAnswer>(sub.answer?.answerJson, {});
		subscriptionSelections.push(`${subAnswer.planKey}:${subAnswer.feeId}`);
	}

	const firstNameBlockValue = findBlockValue({ fieldMeta, answers, questionName: 'firstName' }) ?? '';
	const lastNameBlockValue = findBlockValue({ fieldMeta, answers, questionName: 'lastName' }) ?? '';
	const emailBlockValue = findBlockValue({ fieldMeta, answers, questionName: 'email' }) ?? '';
	const stringifiedIntentDetails = useDebounce(JSON.stringify(firstNameBlockValue + lastNameBlockValue + emailBlockValue), 1000);

	// If there is a condition check it matches the required value before rendering the question
	const conditionValid = useConditionEvaluator(fieldProps);

	const [discountError, setDiscountError] = useState<boolean>(false);
	const [discountErrorReason, setDiscountErrorReason] = useState<string | undefined>(undefined);
	const applyDiscount = async (discountToApply: string) => {
		try {
			if ((!props.token && !inLocalSurveyMode) || formRenderMode === FormRenderMode.preview) return;

			const response = await validateDiscount({
				discountToApply,
				ownerId: owner?.ownerId,
				subscriptionSelections,
				token: props.token
			});
			if (response?.success) {
				answerQuestion({
					...selectedAnswer,
					data: {
						...selectedAnswer?.data,
						discounts: [stripTypenames(response.discount)]
					} as PaymentAnswerData,
					questionName: fieldProps.name,
					type: PAYMENT_BLOCK_ANSWER_TYPE
				});
				setDiscountError(false);
				setDiscountErrorReason(undefined);
				setDiscountCode('');
			} else {
				setDiscountError(true);
				setDiscountErrorReason(response?.failReason);
			}
		} catch (e) {
			setDiscountError(true);
			setDiscountErrorReason(undefined);
			console.error('error: ', e);
		}
	};

	if (!conditionValid) {
		return null;
	}

	if (providersLoading || currenciesLoading || taxRatesLoading) return <Skeleton height="10rem" />;

	if (providersError || currenciesError || taxRatesError) {
		return <MuiAlert severity="error">Something went wrong loading payment details</MuiAlert>;
	}
	if (parsedAnswer?.successfulPaymentToken) {
		return <MuiAlert severity="success">Payment details have been processed. Subsequent submissions will not make further charges.</MuiAlert>;
	}

	const allowMultipleDiscounts = false; // to be prop

	const { grandTotal, totalPayImmediate, totalPayNotImmediate, lineItemsDataImmediate, lineItemsDataNotImmediate } = getPaymentAmounts(
		sections,
		answers ?? [],
		selectedProvider?.method,
		fieldMeta,
		fieldProps.name,
		layout,
		taxRates
	);
	const discountAlreadyEntered = discounts?.some(d => d.code === discountCode);
	const detailsSavedNotPaid = (parsedAnswer.success || parsedAnswer.methodId) && !parsedAnswer.successfulPaymentToken;
	const hasAmountToPay = (lineItemsDataImmediate?.length || lineItemsDataNotImmediate?.length) && grandTotal > 0;

	// Maybe should take into account if actually has payment amount.
	const canPromptForPayment = fieldMeta?.alwaysCaptureMethod || formRenderMode !== FormRenderMode.runtime ? true : hasAmountToPay;

	if (!canPromptForPayment || (!selectedProvider && !readyToQuery)) return null;
	return (
		<FormPaymentContext.Provider
			value={{
				grandTotal,
				totalPayImmediate,
				totalPayNotImmediate,
				lineItemsDataImmediate,
				lineItemsDataNotImmediate,
				submitForm: formSubmit
			}}
		>
			<VerticalInset>
				<FlexColumnSpaced style={{ pointerEvents: formRenderMode === FormRenderMode.design ? 'none' : 'all' }}>
					{availableMethods?.length > 1 && !detailsSavedNotPaid ? (
						<Select
							options={availableMethods}
							value={selectedAnswer?.data?.method}
							getOptionLabel={(o: PaymentMethod) => FRIENDLY_PAYMENT_METHODS[o]}
							label={'Select payment method'}
							size="small"
							onChange={(e, v) => {
								const updatedAnswer = {
									...parsedAnswer,
									intent: undefined,
									success: undefined,
									method: undefined,
									methodId: undefined
								};
								answerQuestion({
									answer: { ...selectedAnswer?.answer, answerJson: JSON.stringify(updatedAnswer) },
									data: {
										...selectedAnswer?.data,
										method: v
									},
									questionName: fieldProps.name,
									type: PAYMENT_BLOCK_ANSWER_TYPE
								});
							}}
						/>
					) : null}
					{selectedProvider?.method === PaymentMethod.Statement &&
					(referenceOverrideType === OverrideOption.Optional || referenceOverrideType === OverrideOption.Required) ? (
						<>
							<TextField
								size="small"
								value={parsedAnswer.invoiceReference}
								required={referenceOverrideType === OverrideOption.Required}
								label={fieldMeta.invoice?.referenceLabelOverride || 'Reference number'}
								onChange={e => {
									const val = e.target.value;
									if (val?.length > 64) return;
									const updatedAnswer = { ...parsedAnswer, invoiceReference: val };
									answerQuestion({
										answer: { answerJson: JSON.stringify(updatedAnswer) },
										data: selectedAnswer?.data,
										questionName: fieldProps.name,
										type: PAYMENT_BLOCK_ANSWER_TYPE
									});
								}}
							/>
							<Divider />
						</>
					) : null}

					{fieldMeta.enableDiscounts ? (
						<FlexColumnSmallSpaced>
							<FlexRowSpacedCentered>
								{allowMultipleDiscounts || discounts?.length === 0 ? (
									<FlexRowSpacedCentered>
										<TextField
											label="Discount code"
											size="small"
											value={discountCode}
											onChange={e => setDiscountCode(e.target.value)}
											disabled={!allowMultipleDiscounts && discounts?.length > 0}
										/>
										<Button
											variant="primary"
											disabled={
												strIsNullOrEmpty(discountCode) ||
												discountAlreadyEntered ||
												(!allowMultipleDiscounts && discounts?.length > 0)
											}
											size="small"
											onClick={() => applyDiscount(discountCode)}
										>
											Apply
										</Button>
									</FlexRowSpacedCentered>
								) : null}
								{discounts?.length > 0 ? (
									<>
										<Typography>Discounts applied: </Typography>
										{discounts.map(discount => {
											return (
												<Chip
													key={discount.code}
													label={discount.code}
													size="small"
													color="primary"
													variant="outlined"
													onDelete={() => {
														setDiscountError(false);
														const newDiscounts = discounts.filter(d => d.codeId !== discount.codeId);
														answerQuestion({
															...selectedAnswer,
															data: {
																...selectedAnswer?.data,
																discounts: newDiscounts
															} as PaymentAnswerData,
															questionName: fieldProps.name,
															type: PAYMENT_BLOCK_ANSWER_TYPE
														});
													}}
												/>
											);
										})}
									</>
								) : null}
							</FlexRowSpacedCentered>

							{discountError ? (
								<MuiAlert severity="error">
									Unable to apply discount{!strIsNullOrEmpty(discountErrorReason) ? ` (${discountErrorReason})` : ''}
								</MuiAlert>
							) : null}
						</FlexColumnSmallSpaced>
					) : null}

					{providersLoading || !isFetched ? (
						<Skeleton height="10rem" />
					) : !selectedProvider && availableMethods?.length > 1 ? (
						<MuiAlert severity="info">Select a payment type</MuiAlert>
					) : !selectedProvider && readyToQuery ? (
						<MuiAlert severity="warning">No payment provider</MuiAlert>
					) : (
						<>
							{detailsSavedNotPaid ? (
								<SavedPaymentDetails
									labelStyles={labelStyles}
									parsedAnswer={parsedAnswer}
									questionName={fieldProps.name}
									selectedAnswer={selectedAnswer}
									selectedProvider={selectedProvider}
								/>
							) : grandTotal === 0 && formRenderMode === FormRenderMode.runtime && isAllowZeroPayment(fieldMeta) ? (
								<ZeroPayment />
							) : PaymentComponents[selectedProvider.providerExtension]?.[selectedProvider.method] ? (
								<IntentAdd
									disableOnError={true}
									key={paymentAttempts + stringifiedIntentDetails} // Force new render/setupIntent for each new payment attempt
									getIntent={async () => {
										const hasSuccessfulPayment = parsedAnswer.successfulPaymentToken;
										const hasSuccessfulIntent =
											parsedAnswer.intent &&
											((selectedProvider.method === PaymentMethod.OneTimePayment && parsedAnswer.intent.intentId) ||
												(detailsSavedNotPaid && !tempDataContext?.dirty));

										// In design mode
										if (formRenderMode !== FormRenderMode.runtime) return DESIGN_MODE_INTENT;
										else if (hasSuccessfulPayment || hasSuccessfulIntent) return tempData.intent;
										// Setup intent successfully and haven't attempted payment
										else {
											// No intent setup or need new
											const intent = await setupIntent({
												formRequestId,
												provider: selectedProvider,
												name: `${firstNameBlockValue} ${lastNameBlockValue}`,
												email: emailBlockValue,
												token: props.token,
												ownerId: target?.ownerId,
												ownerType: target?.ownerType
											});
											if (!intent)
												throw new Error(
													'Unable to setup payment details. Please ensure contact details are filled in to re-attempt.'
												);
											const updatedAnswer = { ...parsedAnswer, intent };
											answerQuestion({
												...selectedAnswer,
												answer: {
													answerJson: JSON.stringify(updatedAnswer)
												},
												questionName: fieldProps.name
											});
											if (tempDataContext) {
												tempDataContext[fieldProps.name] = { intent };
											}
											return intent;
										}
									}}
									onSuccess={result => {
										// We cannot update form answers at this point as we have already entered the form submit function utilising the answer state
										if (tempDataContext) {
											tempDataContext[fieldProps.name] = { ...parsedAnswer, ...(result as any) };
										}
										return result;
									}}
									component={PaymentComponents[selectedProvider.providerExtension]?.[selectedProvider.method]}
									completeIntent={async (intentId, data) => {
										setError(false);
										if (
											selectedProvider.method === PaymentMethod.CreditCard ||
											selectedProvider.method === PaymentMethod.OneTimePayment
										) {
											// This is a lineItems test amount to simulate bank response 05 - Do not honor
											// More found here: https://www.bpoint.com.au/developers/v5/reference/bank-response-codes
											// lineItemsData.map(item => {
											// 	return { ...item, amount: paymentAttempts === 0 ? 10005 : 10000 };
											// }),
											const res = await completeCardIntent({
												formRequestId,
												intentId,
												data,
												provider: selectedProvider,
												token: props.token
											});
											if (!res?.success) {
												setError(true);
												setPaymentAttempts(prev => prev + 1);
											}
											return res as any; // This is meant to be a number so the types are grumpy
										} else if (selectedProvider.method === PaymentMethod.DirectDebit) {
											const res = await completeDebit({ formRequestId, intentId, data, token: props.token });
											if (!res || !res?.success) {
												setError(true);
												setPaymentAttempts(prev => prev + 1);
											}
											return res;
										}
									}}
									updateIntent={async (intentId, amount) => {
										setError(false);
										if (formRenderMode !== FormRenderMode.runtime) return DESIGN_MODE_INTENT;
										const intent = await updateIntent({
											formRequestId,
											provider: selectedProvider,
											amount,
											intentId,
											token: props.token
										});
										const updatedAnswer = { ...parsedAnswer, intent };
										answerQuestion({
											...selectedAnswer,
											answer: {
												answerJson: JSON.stringify(updatedAnswer)
											},
											questionName: fieldProps.name
										});
										if (tempDataContext) {
											tempDataContext[fieldProps.name] = { intent };
										}
										return intent;
									}}
									provider={selectedProvider}
									settings={JSON.parse(selectedProvider.settings ?? '{}')}
									showSave={false}
								/>
							) : selectedProvider.method === STATEMENT_PROVIDER.method ? (
								<div>You will be automatically invoiced.</div>
							) : (
								<DebitForm
									submitFunc={async ({ accountName, accountNumber, bsb }) => {
										setError(false);
										if (formRenderMode !== FormRenderMode.runtime) return;
										const res = await completeDebit({
											formRequestId,
											accountName,
											accountNumber,
											bsb,
											provider: selectedProvider,
											token: props.token
										});
										if (!res || !res?.success) {
											setError(true);
											setPaymentAttempts(prev => prev + 1);
										}
										if (tempDataContext) {
											tempDataContext[fieldProps.name] = res;
										}
										return res;
									}}
									showSave={false}
								/>
							)}

							{error ? <MuiAlert severity="error">{`Error processing payment`}</MuiAlert> : null}
							<PaymentTotalsSummary labelStyles={labelStyles} defaultCurrency={currencies?.default} taxRates={taxRates} />
						</>
					)}
				</FlexColumnSpaced>
			</VerticalInset>
		</FormPaymentContext.Provider>
	);
};

export const buildCardPaymentBlockPublic = (token?: string): IBlockDefinition => {
	return {
		type: 'cardpayment',
		Component: pp => {
			return <PaymentBlockRuntime {...pp} token={token} />;
		}
	};
};
